import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CategoryCascader from '@/shein-platform/components/CategoryCascader';
import { flattenLeafCategories } from '@/shein-platform/utils/sheinCategoryTree';

const CASCADER_COLUMN_WIDTH = 220;
const CASCADER_PANEL_GUTTER = 16;

/**
 * 发品/模板用类目下拉：展开为多列级联；输入关键字时按完整路径过滤末级类目。
 * includeAllOption：面板顶部可选「全部类目」（自动规则等场景）。
 */
export default function CategoryCascaderSelect({
  categories = [],
  value = '',
  label = '',
  onChange,
  placeholder = fixedUi("请选择或搜索类目"),
  disabled = false,
  allowClear = true,
  emptyText = fixedUi("暂无可发商品分类"),
  className = '',
  includeAllOption = false,
  allOptionValue = '*',
  allOptionLabel = fixedUi("全部类目"),
}) {
  const rootRef = useRef(null);
  const controlRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [panelStyle, setPanelStyle] = useState({});
  const [blockAutofill, setBlockAutofill] = useState(true);
  const [columnCount, setColumnCount] = useState(1);

  const leafOptions = useMemo(() => flattenLeafCategories(categories), [categories]);
  const isAllSelected = includeAllOption && String(value || '') === String(allOptionValue);
  const selected = useMemo(
    () => leafOptions.find((opt) => String(opt.categoryId) === String(value)) || null,
    [leafOptions, value],
  );
  const displayLabel = isAllSelected ? allOptionLabel : (selected?.label || label || '');

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return [];
    const leaves = leafOptions.filter((opt) => String(opt.label || '').toLowerCase().includes(q));
    if (includeAllOption && String(allOptionLabel).toLowerCase().includes(q)) {
      return [{ categoryId: allOptionValue, label: allOptionLabel }, ...leaves];
    }
    return leaves;
  }, [leafOptions, keyword, includeAllOption, allOptionValue, allOptionLabel]);

  const showingSearch = keyword.trim().length > 0;

  const updatePanelPosition = () => {
    const el = controlRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const view = window.visualViewport;
    const viewW = view?.width ?? window.innerWidth;
    const viewH = view?.height ?? window.innerHeight;
    const viewLeft = view?.offsetLeft ?? 0;
    const viewTop = view?.offsetTop ?? 0;
    const viewRight = viewLeft + viewW;
    const viewBottom = viewTop + viewH;
    const maxWidth = Math.max(160, viewW - CASCADER_PANEL_GUTTER);
    const cascaderWidth = Math.max(
      rect.width,
      Math.max(1, columnCount) * CASCADER_COLUMN_WIDTH + 2,
    );
    const width = Math.min(
      showingSearch ? Math.max(rect.width, 320) : cascaderWidth,
      maxWidth,
    );
    let left = rect.left;
    if (left + width > viewRight - 8) {
      left = Math.max(viewLeft + 8, viewRight - 8 - width);
    }
    const spaceBelow = viewBottom - rect.bottom - 12;
    const spaceAbove = rect.top - viewTop - 12;
    const minComfort = 240;
    const preferBelow = spaceBelow >= minComfort || spaceBelow >= spaceAbove;
    const maxHeight = Math.min(360, Math.max(140, preferBelow ? spaceBelow : spaceAbove));
    setPanelStyle({
      position: 'fixed',
      top: preferBelow ? rect.bottom + 4 : undefined,
      bottom: preferBelow ? undefined : Math.max(8, viewH - (rect.top - viewTop) + 4),
      left,
      width,
      maxWidth,
      maxHeight,
      // 级联模式给固定高度，否则列 max-height:100% 无法生效，二级类目无法纵向滚动
      height: showingSearch ? undefined : maxHeight,
      overflowX: showingSearch ? 'hidden' : 'auto',
      overflowY: showingSearch ? 'auto' : 'hidden',
      zIndex: 13000,
    });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePanelPosition();
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    window.visualViewport?.addEventListener('resize', onReposition);
    window.visualViewport?.addEventListener('scroll', onReposition);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
      window.visualViewport?.removeEventListener('resize', onReposition);
      window.visualViewport?.removeEventListener('scroll', onReposition);
    };
  }, [open, showingSearch, filtered.length, categories, columnCount]);

  useLayoutEffect(() => {
    if (!open || showingSearch) return;
    const panel = panelRef.current;
    if (!panel) return;
    if (panel.scrollWidth > panel.clientWidth + 1) {
      panel.scrollLeft = panel.scrollWidth;
    }
  }, [open, showingSearch, columnCount, panelStyle.width]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inRoot && !inPanel) {
        setOpen(false);
        setKeyword('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (categoryId) => {
    const next = categoryId == null || categoryId === '' ? '' : String(categoryId);
    setOpen(false);
    setKeyword('');
    if (String(value || '') === next) return;
    onChange?.(next);
  };

  const displayValue = open ? keyword : displayLabel;

  const panel = open && !disabled ? createPortal(
    <div
      ref={panelRef}
      className={`spo-search-select__panel spo-search-select__panel--portal spo-category-cascader-select__panel${showingSearch ? ' is-search' : ''}`}
      style={panelStyle}
      data-testid="shein-category-cascader-select-panel"
      onMouseDown={(e) => e.preventDefault()}
    >
      {showingSearch ? (
        filtered.length === 0 ? (
          <div className="spo-search-select__empty">{fixedUi("无匹配类目")}</div>
        ) : filtered.map((opt) => {
          const active = String(opt.categoryId) === String(value);
          return (
            <button
              key={opt.categoryId}
              type="button"
              className={`spo-search-select__option${active ? ' is-on' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(opt.categoryId)}
            >
              {opt.label}
            </button>
          );
        })
      ) : leafOptions.length === 0 && !includeAllOption ? (
        <div className="spo-search-select__empty">{emptyText}</div>
      ) : (
        <>
          {includeAllOption ? (
            <button
              type="button"
              className={`spo-search-select__option spo-category-cascader-select__all${isAllSelected ? ' is-on' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(allOptionValue)}
            >
              {allOptionLabel}
            </button>
          ) : null}
          {leafOptions.length === 0 ? (
            <div className="spo-search-select__empty">{emptyText}</div>
          ) : (
            <CategoryCascader
              categories={categories}
              selectedCategoryId={isAllSelected ? '' : value}
              hideDetail
              onColumnsChange={setColumnCount}
              onChange={(payload) => {
                if (!payload?.categoryId) return;
                pick(String(payload.categoryId));
              }}
            />
          )}
        </>
      )}
    </div>,
    document.body,
  ) : null;

  const openPanel = () => {
    if (disabled) return;
    if (blockAutofill) setBlockAutofill(false);
    setOpen(true);
    setKeyword('');
  };

  return (
    <div
      className={`spo-search-select spo-category-cascader-select${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}${className ? ` ${className}` : ''}`}
      ref={rootRef}
      data-testid="shein-category-cascader-select"
    >
      <div
        className="spo-search-select__control"
        ref={controlRef}
        onMouseDown={(e) => {
          if (disabled) return;
          if (e.button !== 0) return;
          if (e.target?.closest?.('.spo-search-select__clear')) return;
          openPanel();
        }}
      >
        <input
          ref={inputRef}
          type="text"
          className="spo-input spo-search-select__input"
          value={displayValue}
          disabled={disabled}
          placeholder={displayLabel || placeholder}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          data-form-type="other"
          readOnly={blockAutofill}
          onFocus={() => {
            openPanel();
          }}
          onBlur={() => {
            setBlockAutofill(true);
          }}
          onChange={(e) => {
            setKeyword(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const first = filtered[0];
              if (first) pick(first.categoryId);
            }
            if (e.key === 'Escape') {
              setOpen(false);
              setKeyword('');
            }
            if (e.key === 'Backspace' && !keyword && allowClear && value) {
              onChange?.('');
            }
          }}
        />
        {allowClear && value && !disabled ? (
          <button
            type="button"
            className="spo-search-select__clear"
            aria-label={fixedUi("清除")}
            onClick={() => {
              onChange?.('');
              setKeyword('');
              inputRef.current?.focus();
            }}
          >
            ×
          </button>
        ) : (
          <span className="spo-search-select__caret" aria-hidden>▾</span>
        )}
      </div>
      {panel}
    </div>
  );
}
